import { Model, type ModelProps } from '@/infra/model'
import { Id } from '@/infra/id'
import { computed, observable } from 'mobx'
import { unixEpoch } from '@/utils/date-util'
import { User } from '@/features/iam/users/User'
import { type Workspace } from '@/features/iam/workspaces/Workspace'
import { type DocumentType } from '@/features/documents/DocumentType'
import { type ExtensionBillingInfo } from '@/features/inventory/ExtensionBillingInfo'

/**
 * Indicates the manner in which the Inventory Item is priced.
 */
export type InventoryItemPricing =
  /**
   * Charged as a fee, which is a flat charge for the client.
   */
  | 'Fee'
  /**
   * Two independent flat charges for both the client and pro.
   */
  | 'Flat'
  /**
   * Two independent rates that markup the job's price and the pro payout
   * amount.
   */
  | 'Markup'
  /**
   * Two independent rates that markdown the job's price and the pro payout
   * amount.
   */
  | 'Markdown'

export type InventoryItemProVisibility = 'Hidden' | 'Visible'

export type InventoryItemProps = ModelProps<InventoryItem>

/**
 * A service item that can be added to a job's scope.
 */
export class InventoryItem extends Model<InventoryItem> {
  id = Id.none

  @observable accessor versionId = Id.none
  @observable accessor shortId = ''
  @observable accessor name = ''
  @observable accessor description = ''
  @observable accessor workspace: Workspace | null = null
  @observable accessor createdBy = User.none
  @observable accessor version = 0
  @observable accessor dateCreated = unixEpoch()
  @observable accessor courtesyAmount = 0
  @observable accessor price = 0
  @observable accessor proPrice = 0
  @observable accessor rootId = Id.none
  @observable accessor archived = false
  @observable accessor priceType: InventoryItemPricing = 'Flat'
  @observable accessor supportingItems: InventoryItemSupportingItem[] = []
  @observable accessor proVisibility: InventoryItemProVisibility = 'Visible'

  @observable accessor typeSpecification: InventoryItemTypeSpecification =
    new InventoryItemDefaultTypeSpecification()

  @computed
  get hidden(): boolean {
    return this.proVisibility === 'Hidden'
  }

  @computed
  get global(): boolean {
    return !this.workspace
  }

  @computed
  get override(): boolean {
    return !this.global && !!this.rootId
  }

  @computed
  get supportingItemsCount() {
    return this.supportingItems.length
  }
}

export type InventoryItemExtensionType = 'Personal' | 'Business' | 'Outsourcing'

export type InventoryItemExtensionBillingType = 'Percentage' | 'FlatFee'

export type ExtensionBillingInfoType = 'Regular' | 'Progression'

export type InventoryItemType =
  'Default' | 'Bookkeeping' | 'Consultation' | 'Tax' | 'Extension'

export interface InventoryItemTypeSpecification {
  type: InventoryItemType
}

export class InventoryItemDefaultTypeSpecification implements InventoryItemTypeSpecification {
  type: InventoryItemType = 'Default'
}

export class InventoryItemBookkeepingTypeSpecification implements InventoryItemTypeSpecification {
  type: InventoryItemType = 'Bookkeeping'

  @observable accessor recurring = false

  constructor(recurring: boolean) {
    this.recurring = recurring
  }
}

export class InventoryItemConsultationTypeSpecification implements InventoryItemTypeSpecification {
  type: InventoryItemType = 'Consultation'

  @observable accessor lengthInMinutes = 0

  constructor(lengthInMinutes: number) {
    this.lengthInMinutes = lengthInMinutes
  }
}

export class InventoryItemTaxTypeSpecification implements InventoryItemTypeSpecification {
  type: InventoryItemType = 'Tax'

  @observable accessor reviewPrice = 0
  @observable accessor reviewPayout = 0

  constructor(reviewPrice: number, reviewPayout: number) {
    this.reviewPrice = reviewPrice
    this.reviewPayout = reviewPayout
  }
}

export class InventoryItemExtensionTypeSpecification implements InventoryItemTypeSpecification {
  type: InventoryItemType = 'Extension'

  extensionType: InventoryItemExtensionType = 'Personal'

  billingType: InventoryItemExtensionBillingType = 'Percentage'

  billingInfo: ExtensionBillingInfo

  constructor(
    extensionType: InventoryItemExtensionType,
    billingType: InventoryItemExtensionBillingType,
    billingInfo: ExtensionBillingInfo,
  ) {
    this.extensionType = extensionType
    this.billingType = billingType
    this.billingInfo = billingInfo
  }
}

export interface InventoryItemSupportingItem {
  required: boolean
  spec: InventoryItemSupportingItemSpec
}

export type InventoryItemSupportingItemSpec =
  | InventoryItemSupportingItemDocumentSpec
  | InventoryItemSupportingItemPersonalInfoSpec
  | InventoryItemSupportingItemSpousalInfoSpec
  | InventoryItemSupportingItemDependentsInfoSpec
  | InventoryItemSupportingItemPersonalRefundMethodInfoSpec
  | InventoryItemSupportingItemBusinessInfoSpec
  | InventoryItemSupportingItemBusinessRefundMethodInfoSpec
  | InventoryItemSupportingItemNotesSpec

/**
 * Gets a key that identifies the supporting item spec among an item's specs.
 *
 * @param spec
 */
export function getSupportingItemSpecKey(
  spec: InventoryItemSupportingItemSpec,
) {
  return spec.type === 'Document' ? spec.documentType.id : spec.type
}

export interface InventoryItemSupportingItemDocumentSpec {
  type: 'Document'
  documentType: DocumentType
}

export interface InventoryItemSupportingItemPersonalInfoSpec {
  type: 'PersonalInfo'
}

export interface InventoryItemSupportingItemSpousalInfoSpec {
  type: 'SpousalInfo'
}

export interface InventoryItemSupportingItemDependentsInfoSpec {
  type: 'DependentsInfo'
}

export interface InventoryItemSupportingItemPersonalRefundMethodInfoSpec {
  type: 'PersonalRefundMethodInfo'
}

export interface InventoryItemSupportingItemBusinessInfoSpec {
  type: 'BusinessInfo'
}

export interface InventoryItemSupportingItemBusinessRefundMethodInfoSpec {
  type: 'BusinessRefundMethodInfo'
}

export interface InventoryItemSupportingItemNotesSpec {
  type: 'Notes'
}

export type InventoryItemSupportingItemSpecTypes =
  | 'Document'
  | 'PersonalInfo'
  | 'SpousalInfo'
  | 'DependentsInfo'
  | 'PersonalRefundMethodInfo'
  | 'BusinessInfo'
  | 'BusinessRefundMethodInfo'
  | 'Notes'
